import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faSave,
  faTimes,
  faCamera,
  faExclamationTriangle,
  faUser,
  faPhone,
  faEnvelope,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  InputGroup,
  Alert,
} from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";

import ProfilePicture from "../../../assets/img/team/profile-picture-3.jpg";

/* ---------- constants remain unchanged ---------- */

const STATE_OPTIONS = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const CITY_OPTIONS = {
  Maharashtra: ["Mumbai", "Pune", "Nagpur", "Nashik"],
  Karnataka: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi"],
  Gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot"],
  Delhi: ["New Delhi"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Varanasi"],
};

const KYC_STATUS_VARIANT = {
  DONE: "success",
  PENDING: "warning",
  REJECTED: "danger",
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_KYC_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const initialProfile = {
  profilePhoto: null,
  fullName: "Bonnie Green",
  mobileNumber: "9876543210",
  emailId: "Bonnie@example.com",
  addressLine1: "Flat 12, Shree Residency",
  addressLine2: "Near Main Market",
  state: "Maharashtra",
  city: "Pune",
  pincode: "416012",
  kycStatus: "DONE",
  panNumber: "ABCDE1234F",
  aadhaarNumber: "123412341234",
  idProofName: "aadhaar-card.pdf",
  bankAccountHolderName: "Bonnie Green",
  bankName: "State Bank of India",
  accountNumber: "123456789012",
  ifscCode: "SBIN0001234",
};

export default function AdminProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [draft, setDraft] = useState(initialProfile);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [profilePreview, setProfilePreview] = useState(ProfilePicture);

  const selectedCities = useMemo(
    () => CITY_OPTIONS[draft.state] || [],
    [draft.state]
  );

  const hasKycSensitiveNameChange = useMemo(() => {
    return (
      profile.fullName.trim() !== draft.fullName.trim() &&
      profile.kycStatus === "DONE"
    );
  }, [profile.fullName, profile.kycStatus, draft.fullName]);

  const openEditMode = () => {
    setDraft(profile);
    setErrors({});
    setAlertMessage("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(profile);
    setErrors({});
    setAlertMessage("");
    setIsEditing(false);
    setProfilePreview(profile.profilePhoto || ProfilePicture);
  };

  /* ---------- input handlers unchanged ---------- */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setDraft((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" ? { city: "" } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleMobileChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setDraft((prev) => ({ ...prev, mobileNumber: nextValue }));
    setErrors((prev) => ({ ...prev, mobileNumber: "" }));
  };

  const handlePincodeChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    setDraft((prev) => ({ ...prev, pincode: nextValue }));
    setErrors((prev) => ({ ...prev, pincode: "" }));
  };

  const handleAadhaarChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 12);
    setDraft((prev) => ({ ...prev, aadhaarNumber: nextValue }));
    setErrors((prev) => ({ ...prev, aadhaarNumber: "" }));
  };

  const handleAccountNumberChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 18);
    setDraft((prev) => ({ ...prev, accountNumber: nextValue }));
    setErrors((prev) => ({ ...prev, accountNumber: "" }));
  };

  /* ---------- file validation ---------- */

  const validateFile = (file) => {
    if (!file) return "Please choose a file.";
    if (!ALLOWED_KYC_MIME_TYPES.includes(file.type)) {
      return "Only PDF, JPG, and PNG files are allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be 2 MB or less.";
    }
    return "";
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, profilePhoto: validationError }));
      toast.error(validationError);
      return;
    }

    setProfilePreview(URL.createObjectURL(file));
    setDraft((prev) => ({ ...prev, profilePhoto: file }));
    setErrors((prev) => ({ ...prev, profilePhoto: "" }));
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, idProof: validationError }));
      toast.error(validationError);
      return;
    }

    setDraft((prev) => ({
      ...prev,
      idProofFile: file,
      idProofName: file.name,
    }));
    setErrors((prev) => ({ ...prev, idProof: "" }));
  };

  /* ---------- validation + save ---------- */

  const validate = () => {
    const nextErrors = {};

    if (!draft.fullName.trim()) nextErrors.fullName = "Full name is required.";

    if (!draft.mobileNumber.trim()) {
      nextErrors.mobileNumber = "Mobile number is required.";
    } else if (!/^[6-9]\d{9}$/.test(draft.mobileNumber)) {
      nextErrors.mobileNumber = "Enter a valid 10-digit mobile number.";
    }

    if (!draft.emailId.trim()) {
      nextErrors.emailId = "Email ID is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      toast.error("Please correct the highlighted errors.");
      return;
    }

    const nextProfile = {
      ...profile,
      ...draft,
      kycStatus: hasKycSensitiveNameChange ? "PENDING" : profile.kycStatus,
    };

    setProfile(nextProfile);
    setDraft(nextProfile);
    setIsEditing(false);

    const message = hasKycSensitiveNameChange
      ? "Profile saved. KYC has been reactivated because the full name changed."
      : "Profile saved successfully.";

    setAlertMessage(message);
    toast.success(message);
  };

  const renderValue = (value) =>
    value && String(value).trim().length > 0 ? value : "N/A" || "-";



  return (
    <>
      <div
        style={{
          overflowX: "hidden",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <div>
            <h2 className="h4 mb-1">Admin Profile</h2>
            {/* <div className="text-muted">View and update your personal, KYC, and bank details.</div> */}
          </div>

          <div className="d-flex gap-2">
            {!isEditing ? (
              <Button variant="primary" onClick={openEditMode} className="submit-btn">
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit Profile
              </Button>
            ) : (
              <>
                <Button onClick={cancelEdit} className="cancel-btn">
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} className="submit-btn">
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {alertMessage ? (
          <Alert variant="info" className="border-0 shadow-sm rounded-4">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {alertMessage}
          </Alert>
        ) : null}

        <Row className="g-4 mx-0">
          <Col xs={12} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-4">
                <div className="text-center">
                  <div className="position-relative d-inline-block mb-3">
                    <img
                      src={profilePreview}
                      alt="Profile"
                      className="rounded-circle border"
                      style={{ width: 120, height: 120, objectFit: "cover" }}
                    />
                    {isEditing ? (
                      <label
                        htmlFor="profilePhoto"
                        className="position-absolute bottom-0 end-0 btn btn-primary btn-sm rounded-circle shadow-sm"
                        style={{ width: 38, height: 38 }}
                      >
                        <FontAwesomeIcon icon={faCamera} />
                      </label>
                    ) : null}
                  </div>

                  {isEditing ? (
                    <Form.Group className="mb-3 text-start">
                      <Form.Label className="fw-semibold">Profile Photo</Form.Label>
                      <Form.Control
                        id="profilePhoto"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleProfilePhotoChange}
                      />
                      {errors.profilePhoto ? (
                        <div className="text-danger small mt-1">{errors.profilePhoto}</div>
                      ) : null}
                    </Form.Group>
                  ) : null}

                  <h4 className="mb-1">{profile.fullName}</h4>
                  <div className="text-muted mb-3">{profile.emailId}</div>

                  <Badge
                    bg={KYC_STATUS_VARIANT[profile.kycStatus] || "secondary"}
                    className="px-3 py-2 rounded-pill"
                  >
                    {profile.kycStatus}
                  </Badge>

                  {profile.kycStatus === "PENDING" ? (
                    <div className="mt-3 text-warning small">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      KYC will be rechecked after profile changes.
                    </div>
                  ) : null}
                </div>

                <hr className="my-4" />

                <div className="small text-muted">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Mobile</span>
                    <span className="text-dark">{profile.mobileNumber}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>State</span>
                    <span className="text-dark">{renderValue(profile.state)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>City</span>
                    <span className="text-dark">{renderValue(profile.city)}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={8} style={{ minWidth: 0 }}>
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-1">Profile Summary</h5>
                    {/* <div className="text-muted small">Basic profile information</div> */}
                  </div>
                  <Badge
                    bg={KYC_STATUS_VARIANT[profile.kycStatus] || "secondary"}
                    className="px-3 py-2 rounded-pill"
                  >
                    KYC {profile.kycStatus}
                  </Badge>
                </div>

                <Row className="g-3 mx-0">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Full Name <span className="text-danger">*</span></Form.Label>
                      {isEditing ? (
                        <InputGroup>
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faUser} />
                          </InputGroup.Text>
                          <Form.Control
                            name="fullName"
                            value={draft.fullName}
                            onChange={handleChange}
                            isInvalid={!!errors.fullName}
                            className="border-start-0"
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.fullName)}</div>
                      )}
                      {errors.fullName ? (
                        <div className="text-danger small mt-1">{errors.fullName}</div>
                      ) : null}
                      {isEditing && hasKycSensitiveNameChange ? (
                        <div className="text-warning small mt-1">
                          Changing the full name will reactivate the KYC process.
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Mobile Number <span className="text-danger">*</span></Form.Label>
                      {isEditing ? (
                        <InputGroup>
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faPhone} />
                          </InputGroup.Text>
                          <Form.Control
                            name="mobileNumber"
                            value={draft.mobileNumber}
                            onChange={handleMobileChange}
                            isInvalid={!!errors.mobileNumber}
                            maxLength={10}
                            className="border-start-0"
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.mobileNumber)}</div>
                      )}
                      {errors.mobileNumber ? (
                        <div className="text-danger small mt-1">{errors.mobileNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Email ID <span className="text-danger">*</span></Form.Label>
                      {isEditing ? (
                        <InputGroup>
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faEnvelope} />
                          </InputGroup.Text>
                          <Form.Control
                            name="emailId"
                            type="email"
                            value={draft.emailId}
                            onChange={handleChange}
                            isInvalid={!!errors.emailId}
                            className="border-start-0"
                            readOnly
                            style={{ background: "#f8f9fa" }}
                          />
                        </InputGroup>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.emailId)}</div>
                      )}
                      {errors.emailId ? (
                        <div className="text-danger small mt-1">{errors.emailId}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="mb-4">Address Details</h5>
                {/* <div className="text-muted small mb-4">Primary and mailing address</div> */}

                <Row className="g-3 mx-0">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Address Line 1</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="addressLine1"
                          value={draft.addressLine1}
                          onChange={handleChange}
                          placeholder="Enter address line 1"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.addressLine1)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Address Line 2</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="addressLine2"
                          value={draft.addressLine2}
                          onChange={handleChange}
                          placeholder="Enter address line 2"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.addressLine2)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">State</Form.Label>
                      {isEditing ? (
                        <Form.Select name="state" value={draft.state} onChange={handleChange}>
                          <option value="">Select state</option>
                          {STATE_OPTIONS.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.state)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  {/* <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">City</Form.Label>
                    {isEditing ? (
                      <Form.Select name="city" value={draft.city} onChange={handleChange}>
                        <option value="">Select city</option>
                        {(selectedCities || []).map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="form-control bg-light">{renderValue(profile.city)}</div>
                    )}
                  </Form.Group>
                </Col> */}

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">City</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="city"
                          value={draft.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.city)}</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Pincode</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="pincode"
                          value={draft.pincode}
                          onChange={handlePincodeChange}
                          maxLength={6}
                          placeholder="Enter pincode"
                          isInvalid={!!errors.pincode}
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.pincode)}</div>
                      )}
                      {errors.pincode ? (
                        <div className="text-danger small mt-1">{errors.pincode}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Body className="p-4">
                <h5 className="mb-4">KYC Details</h5>
                {/* <div className="text-muted small mb-4">Identity and document details</div> */}

                <Row className="g-3 mx-0">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">PAN Number</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="panNumber"
                          value={draft.panNumber}
                          onChange={handleChange}
                          placeholder="Enter PAN"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.panNumber)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Aadhaar Number</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="aadhaarNumber"
                          value={draft.aadhaarNumber}
                          onChange={handleAadhaarChange}
                          placeholder="Enter Aadhaar number"
                          maxLength={12}
                          isInvalid={!!errors.aadhaarNumber}
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {profile.aadhaarNumber
                            ? `XXXX XXXX ${profile.aadhaarNumber.slice(-4)}`
                            : "N/A" || "-"}
                        </div>
                      )}
                      {errors.aadhaarNumber ? (
                        <div className="text-danger small mt-1">{errors.aadhaarNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold d-block">KYC Status</Form.Label>
                      <Badge
                        bg={KYC_STATUS_VARIANT[profile.kycStatus] || "secondary"}
                        className="px-3 py-2 rounded-pill"
                      >
                        {profile.kycStatus}
                      </Badge>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">ID Proof Upload</Form.Label>
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleIdProofChange}
                          />
                          <div className="form-text">Allowed: PDF, JPG, PNG. Max size: 2 MB.</div>
                          {errors.idProof ? (
                            <div className="text-danger small mt-1">{errors.idProof}</div>
                          ) : null}
                        </>
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.idProofName)}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <h5 className="mb-4">Bank Details</h5>
                {/* <div className="text-muted small mb-4">Payout and settlement details</div> */}

                <Row className="g-3 mx-0">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Account Holder Name</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="bankAccountHolderName"
                          value={draft.bankAccountHolderName}
                          onChange={handleChange}
                          placeholder="Enter account holder name"
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {renderValue(profile.bankAccountHolderName)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Bank Name</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="bankName"
                          value={draft.bankName}
                          onChange={handleChange}
                          placeholder="Enter bank name"
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.bankName)}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Account Number</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="accountNumber"
                          value={draft.accountNumber}
                          onChange={handleAccountNumberChange}
                          placeholder="Enter account number"
                          isInvalid={!!errors.accountNumber}
                        />
                      ) : (
                        <div className="form-control bg-light">
                          {profile.accountNumber
                            ? `XXXXXX${profile.accountNumber.slice(-4)}`
                            : "N/A" || "-"}
                        </div>
                      )}
                      {errors.accountNumber ? (
                        <div className="text-danger small mt-1">{errors.accountNumber}</div>
                      ) : null}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">IFSC Code</Form.Label>
                      {isEditing ? (
                        <Form.Control
                          name="ifscCode"
                          value={draft.ifscCode}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                          isInvalid={!!errors.ifscCode}
                          style={{ textTransform: "uppercase" }}
                        />
                      ) : (
                        <div className="form-control bg-light">{renderValue(profile.ifscCode)}</div>
                      )}
                      {errors.ifscCode ? (
                        <div className="text-danger small mt-1">{errors.ifscCode}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>

                {isEditing ? (
                  <div className="d-flex justify-content-end mt-4">
                    <Button variant="primary" onClick={handleSave} className="submit-btn">
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Save Updated Profile
                    </Button>
                  </div>
                ) : null}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <style>
{`
  .submit-btn {
    background: linear-gradient(90deg, #2c60ba 0%, #005dfb 100%);
    border: none;
    color: #ffffff;
  },

  // .cancel-btn {
  //  background: #ffffff;
  //   border: 1px solid #d61515;
  //   color: #d61515;
  // }

`}
</style>
    </>
  );
}